import { useEffect, useState } from "react";
import { api } from "../services/api";
import AdminNavbar from "../components/AdminNavbar";
import "./AdminUsers.css";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    document.title = "Admin Users | Balitah";

    let descriptionTag = document.querySelector('meta[name="description"]');
    if (!descriptionTag) {
      descriptionTag = document.createElement("meta");
      descriptionTag.setAttribute("name", "description");
      document.head.appendChild(descriptionTag);
    }

    descriptionTag.setAttribute(
      "content",
      "Manage user accounts on Balitah, a local Bruneian e-commerce platform.",
    );
  }, []);

  const fetchUsers = async () => {
    try {
      const data = await api.getAdminUsers();
      setUsers(data.data || []);
    } catch (error) {
      console.error("Failed to load users:", error);
      alert("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const deleteUser = async (id) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this user?",
    );

    if (!confirmed) return;

    setDeletingId(id);
    try {
      await api.deleteAdminUser(id);
      await fetchUsers();
    } catch (error) {
      console.error("Failed to delete user:", error);
      alert("Failed to delete user");
    } finally {
      setDeletingId(null);
    }
  };

  const normalizedSearch = searchText.trim().toLowerCase();
  const roleOptions = [
    "all",
    ...Array.from(new Set(users.map((user) => user.role).filter(Boolean))),
  ];

  const filteredUsers = users.filter((user) => {
    const username = String(user.username || "").toLowerCase();
    const email = String(user.email || "").toLowerCase();

    const matchesSearch =
      !normalizedSearch ||
      username.includes(normalizedSearch) ||
      email.includes(normalizedSearch);

    const matchesRole = roleFilter === "all" || user.role === roleFilter;

    return matchesSearch && matchesRole;
  });

  const getRoleBadgeClass = (role) => {
    switch (role?.toLowerCase()) {
      case "admin":
        return "au-badge--admin";
      case "seller":
        return "au-badge--seller";
      case "buyer":
      default:
        return "au-badge--buyer";
    }
  };

  return (
    <div className="au-page">
      <AdminNavbar />

      <div className="au-container">
        {/* Header */}
        <div className="au-header">
          <div className="au-header-content">
            <h1>User Management</h1>
            <p>View and manage all registered users</p>
          </div>
          <div className="au-header-stats">
            <div className="au-stat">
              <span className="au-stat-value">{users.length}</span>
              <span className="au-stat-label">Total Users</span>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="au-filters">
          <div className="au-search-wrap">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              className="au-search"
              placeholder="Search by username or email..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            {searchText && (
              <button
                className="au-clear-btn"
                onClick={() => setSearchText("")}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>

          <div className="au-select-wrap">
            <select
              className="au-select"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              {roleOptions.map((role) => (
                <option key={role} value={role}>
                  {role === "all"
                    ? "All Roles"
                    : role.charAt(0).toUpperCase() + role.slice(1)}
                </option>
              ))}
            </select>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </div>
        </div>

        {/* Results Info */}
        {!loading && (
          <div className="au-results-info">
            Showing <strong>{filteredUsers.length}</strong> of{" "}
            <strong>{users.length}</strong> users
            {(searchText || roleFilter !== "all") && (
              <button
                className="au-reset-btn"
                onClick={() => {
                  setSearchText("");
                  setRoleFilter("all");
                }}
              >
                Clear filters
              </button>
            )}
          </div>
        )}

        {/* Table */}
        <div className="au-table-card">
          {loading ? (
            <div className="au-loading">
              <div className="au-spinner"></div>
              <p>Loading users...</p>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="au-empty">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <line x1="17" y1="11" x2="23" y2="11" />
              </svg>
              <h3>No users found</h3>
              <p>Try adjusting your search or filter criteria</p>
            </div>
          ) : (
            <div className="au-table-wrap">
              <table className="au-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((u) => (
                    <tr key={u._id}>
                      <td>
                        <div className="au-user-cell">
                          <div className="au-avatar">
                            {(u.username || "U").charAt(0).toUpperCase()}
                          </div>
                          <span className="au-username">{u.username}</span>
                        </div>
                      </td>
                      <td>
                        <span className="au-email">{u.email}</span>
                      </td>
                      <td>
                        <span
                          className={`au-badge ${getRoleBadgeClass(u.role)}`}
                        >
                          {u.role}
                        </span>
                      </td>
                      <td>
                        <button
                          className="au-delete-btn"
                          onClick={() => deleteUser(u._id)}
                          disabled={deletingId === u._id}
                        >
                          {deletingId === u._id ? (
                            <div className="au-btn-spinner"></div>
                          ) : (
                            <svg
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <polyline points="3 6 5 6 21 6" />
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            </svg>
                          )}
                          <span>Delete</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
